import { faLock, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Typography,
} from '@mui/material';
import MfaRecoveryList from 'components/MfaRecovery/MfaRecoveryList';
import { useGetMfaRecoveryOtPsLazyQuery } from 'lib/graphql/graphql';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import {
  DialogBlockGrid,
  MfaForm,
  MobileFriendlyMuiOtpInput,
} from '../../styles';
import { FullWidthContainedButton } from './styles';

export interface ViewRecoveryCodesFormProps {
  onClose: () => void
}

interface ViewRecoveryCodesFormData {
  code: string
}

export default function ViewRecoveryCodesForm({ onClose }: ViewRecoveryCodesFormProps) {
  const [getCodes, {
    called,
    data,
    loading,
    error,
  }] = useGetMfaRecoveryOtPsLazyQuery();
  const formControl = useForm<ViewRecoveryCodesFormData>({ mode: 'onTouched' });
  const [codeError, setCodeError] = useState<boolean>(false);
  const {
    handleSubmit,
    watch,
  } = formControl;

  const watchCode = watch('code');

  useEffect(() => {
    setCodeError(error !== undefined);
  }, [error]);

  useEffect(() => {
    setCodeError(false);
  }, [watchCode]);

  const onSubmit: SubmitHandler<ViewRecoveryCodesFormData> = useCallback(async (formData) => {
    await getCodes({
      variables: {
        totp: formData.code,
      },
    });
  }, [getCodes]);

  const codes = useMemo<string[]>(() => {
    if (data?.getMfaRecoveryOTPs) {
      return data.getMfaRecoveryOTPs.recoveryOtps.map(({ code }) => code);
    }
    return [];
  }, [data]);

  return (
    <FormProvider {...formControl}>
      <MfaForm onSubmit={handleSubmit(onSubmit)}>
        {(!called || error) && (
          <>
            <Grid container flexDirection="row" alignItems="center" paddingLeft={2}>
              <Grid item>
                <FontAwesomeIcon icon={faLock} />
              </Grid>
              <Grid item>
                <DialogTitle>
                  <FormattedMessage id="confirm_access" />
                </DialogTitle>
              </Grid>
            </Grid>
            <DialogContent>
              <DialogBlockGrid container>
                <Grid item display="flex" justifyContent="center">
                  <FontAwesomeIcon icon={faMobile} size="3x" />
                </Grid>
                <Grid item display="flex" justifyContent="center">
                  <Typography variant="h6">
                    <FormattedMessage id="mfa_code" />
                  </Typography>
                </Grid>
                <Grid item display="flex" justifyContent="center" paddingTop={2} paddingBottom={2}>
                  <Controller
                    name="code"
                    control={formControl.control}
                    rules={{ validate: (value) => value?.length === 6 }}
                    render={({ field, fieldState }) => (
                      <Box>
                        <MobileFriendlyMuiOtpInput
                          {...field}
                          sx={{ gap: 1 }}
                          length={6}
                        />
                        {fieldState.invalid ? (
                          <FormHelperText error>
                            <FormattedMessage id="mfa_code_invalid" />
                          </FormHelperText>
                        ) : null }
                        {codeError && (
                          <Alert severity="error" sx={{ marginTop: 2 }} onClose={() => setCodeError(false)}>
                            <FormattedMessage id="mfa_view_code_error" />
                          </Alert>
                        )}
                      </Box>
                    )}
                  />
                </Grid>
                <Grid item display="flex" justifyContent="center" padding={2}>
                  <LoadingButton loading={loading} type="submit" variant="contained" color="secondary" size="large" sx={{ width: '100%' }}>
                    <FormattedMessage id="mfa_verify" />
                  </LoadingButton>
                </Grid>
                <Grid item display="flex" justifyContent="flex-start" paddingRight={2} paddingLeft={2} paddingBottom={2}>
                  <FormattedMessage id="mfa_code_info" />
                </Grid>
              </DialogBlockGrid>
            </DialogContent>
          </>
        )}
        {(called && codes.length) && (
          <>
            <Grid container flexDirection="row">
              <Grid item xs={12}>
                <DialogTitle>
                  <FormattedMessage id="mfa_codes_tilte" />
                </DialogTitle>
              </Grid>
            </Grid>
            <Grid container flexDirection="column">
              <Grid item xs={12}>
                <DialogBlockGrid container>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <FormattedMessage id="mfa_codes_info" />
                    </Alert>
                  </Grid>
                  <MfaRecoveryList
                    otps={codes}
                  />
                </DialogBlockGrid>
              </Grid>
            </Grid>
            <Grid item xs={12} paddingTop={2}>
              <FormattedMessage id="mfa_codes_reset_instructions" />
            </Grid>
            <Grid item xs={12} paddingTop={2}>
              <FullWidthContainedButton onClick={onClose} color="secondary">
                <FormattedMessage id="general_close" />
              </FullWidthContainedButton>
            </Grid>
          </>
        )}
      </MfaForm>
    </FormProvider>
  );
}
